/*
 * Copyright 2022 Objectiv B.V.
 */

import {
  BrowserTrackerConfig,
  ContextsConfig,
  makeBrowserTrackerDefaultPluginsList,
  makeBrowserTrackerDefaultQueue,
  makeBrowserTrackerDefaultTransport,
  Tracker,
  TrackerConfig,
} from '@objectiv/tracker-browser';
import { TrackerPlatform } from '@objectiv/tracker-core';

/**
 * Angular Tracker is identical to BrowserTracker, exception made for its platform.
 */
export class AngularTracker extends Tracker {
  // A copy of the original configuration
  readonly trackerConfig: TrackerConfig;

  constructor(trackerConfig: BrowserTrackerConfig, ...contextConfigs: ContextsConfig[]) {
    let config: TrackerConfig = trackerConfig;

    // Set the platform
    config.platform = TrackerPlatform.ANGULAR;

    // Either `transport` or `endpoint` must be provided
    if (!config.transport && !trackerConfig.endpoint) {
      throw new Error('Either `transport` or `endpoint` must be provided');
    }

    // `transport` and `endpoint` must not be provided together
    if (config.transport && trackerConfig.endpoint) {
      throw new Error('Please provider either `transport` or `endpoint`, not both at same time');
    }

    // Automatically create a default Transport for the given `endpoint` with a sensible setup
    if (trackerConfig.endpoint) {
      config = {
        ...config,
        transport: makeBrowserTrackerDefaultTransport(),
        queue: config.queue ?? makeBrowserTrackerDefaultQueue(config),
      };
    }

    // Add default plugins for Angular
    config.plugins = [...makeBrowserTrackerDefaultPluginsList(trackerConfig), ...(config.plugins ?? [])];

    // Initialize core Tracker
    super(config, ...contextConfigs);

    // Store original config for comparison with other instances of Browser Tracker
    this.trackerConfig = trackerConfig;
  }
}
